import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { asStr, getLegacyDomain } from '@paytrace/pt-skel/Helper'
import moment from 'moment'

import { EDV2_PAGE_DEFINITIONS } from '@paytrace/pt-skel/Helper/Constants'
import { getPageURLs } from '@paytrace/pt-skel/Helper/utils'
import { DataWithKey } from '../Component/EnhancedData/Types'
import Event from '../Event'
import { SelfKVPair } from '../types'

export function isCardExpired (exp_month: string, exp_year: string): boolean {
  const now = new Date()
  const year = Number(String(now.getFullYear()).slice(2))
  const month = now.getMonth() + 1

  if (year < Number(exp_year)) {
    return false
  }

  if (year > Number(exp_year)) {
    return true
  }

  if (month > Number(exp_month)) {
    return true
  }

  return false
}

/**
 * Formats a raw date string into 12 hour format: MM/DD/YYYY hh:mm:ss am/pm
 */
export function formatDateTime (dateString: string, defaultDateTime = ''): string {
  const date = moment(dateString)

  if (!date.isValid()) {
    return defaultDateTime
  }

  return date.format('MM/DD/YYYY hh:mm:ss a')
}

export function parseDateWithMoment (dateString:string, format: string) {
  // Use moment to parse the date based on the specified format
  const date = moment(dateString, format)

  // Check if the date is valid
  if (date.isValid()) {
    // Return a native JavaScript Date object
    return date.toDate()
  } else {
    console.error('invalid date format: ', dateString)
    return null
  }
}

export function parseDateFromDDMMYY (dateString: string) {
  return parseDateWithMoment(dateString, 'DD-MM-YY')
}

export function parseDateFromMMDDYY (dateString: string) {
  return parseDateWithMoment(dateString, 'MM-DD-YY')
}

export function parseDateFromYYYYMMDD (dateString: string) {
  return parseDateWithMoment(dateString, 'YYYY-MM-DD')
}

// Utility function to format the amount as currency with 2 decimals
export const formatCurrency = (amount: number): string => {
  return '$' + amount.toFixed(2)
}

export function sanitizeNumber (value: string): string {
  // Remove non-numeric characters except for the decimal point
  let cleanValue = value.replace(/[^0-9.]/g, '')

  // Ensure only one decimal point is present
  if ((cleanValue.match(/\./g) || []).length > 1) {
    // Keep only the first decimal point and remove the others
    const firstDotIndex = cleanValue.indexOf('.')
    cleanValue = cleanValue.slice(0, firstDotIndex + 1) + cleanValue.slice(firstDotIndex + 1).replace(/\./g, '')
  }
  // Limit to 2 decimal places if there is a decimal point
  if (cleanValue.includes('.')) {
    // Split the number into the integer part and the decimal part
    const [integerPart, decimalPart] = cleanValue.split('.')
    // Limit decimal part to 2 digits
    cleanValue = `${integerPart}.${decimalPart.slice(0, 2)}`
  }
  // Remove leading zeros, but keep "0" as it is
  if (cleanValue.startsWith('0') && cleanValue.length > 1) {
    cleanValue = cleanValue.replace(/^0+/, '')
  }
  // Special case for '0.' (e.g., if the user starts typing '0.' or '.')
  if (cleanValue.startsWith('.')) {
    cleanValue = '0' + cleanValue
  }

  return cleanValue
}

export const padZero = (value: string | number, padLength: number): string => {
  return String(value).padStart(padLength, '0')
}

export const padZeroTwoDigits = (value: string | number): string => {
  return padZero(value, 2)
}

export function getActiveMenuSection (pathname: string): string {
  const currentPath = pathname.toLowerCase()
  if (currentPath === '/') {
    return 'Dashboard'
  }
  const edv2Paths: string[] = getPageURLs(EDV2_PAGE_DEFINITIONS)
  if (edv2Paths.some(p => currentPath.includes(p))) {
    return 'Enhanced Data'
  }
  return 'Virtual Terminal'
}

export function defaultNetworkErrorHandler (error: Error): void {
  console.debug(error)
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

export const clearNetworkError = (): void => PubSub.pub(Event.NETWORK_ERROR_CLEAR)
export const raiseNetworkError = (errorMessage: string): void => PubSub.pub(Event.NETWORK_ERROR, new Error(errorMessage))

export const getViewTransactionDetailHref = (transactionId: number): string => {
  return `${getLegacyDomain()}/view.pay?b=${transactionId}`
}

export function toMap <T extends object> (itemsWithKey: DataWithKey<T>[]): Record<string, DataWithKey<T>> {
  return Object.fromEntries(itemsWithKey.map((item) => [item.key, item]))
}

export function toMapByKey <T extends object> (itemsWithKey: T[], keyField: keyof T): SelfKVPair<T> {
  return Object.fromEntries(itemsWithKey.map((item) => [item[keyField], item]))
}

export const camelToKebab = (str: string) => {
  return str
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2') // Insert a hyphen between lower/number and uppercase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2') // Handle acronyms like 'getHTMLData' -> 'get-html-data'
    .toLowerCase()
}

export function toCSSClassNameWithSuffix<T extends object> (fieldName: keyof T, ...suffixArgs: string[]): string {
  const suffix: string = suffixArgs.length ? `-${suffixArgs.join('-')}` : ''
  return camelToKebab(asStr(fieldName)) + suffix
}

export const extractActualErrorMessage = (err: any) => {
  const errors: any = err?.errors?.input
  if (errors === undefined) {
    return err.message
  }
  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join()
  }
}

export function formatDateToMMDDYYYY (date: Date) {
  const formatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/

  if (formatRegex.test(date)) {
    return date
  }
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${month}/${day}/${year}`
}

export function formatDateToYYYYMMDD (date: Date) {
  if (!date) return undefined
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${year}-${month}-${day}`
}

export function parseDateFromMDYYYY (dateString: string) {
  return parseDateWithMoment(dateString, 'M/D/YYYY')
}
